import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DragDropProvider,
  DragOverlay,
  type DragEndEvent,
  type DragMoveEvent,
} from "@dnd-kit/react";
import {
  PanelsTopLeft,
  Plus,
  Moon,
  Sun,
  Monitor,
  Settings as SettingsIcon,
  Shield,
  Undo2,
  WifiOff,
  X,
} from "lucide-react";
import {
  addDays,
  daysBetween,
  shortDate,
  type Entity,
  type Kind,
  type Snapshot,
  type User,
} from "@kkindex/shared";
import { api, ApiError } from "./api";
import { Editor, type Execute } from "./Editor";
import { Calendar } from "./Calendar";
import { Tasks } from "./Tasks";
import { taskColumns } from "./taskLayout";
import { SelectionProvider } from "./interaction";
import { Draggable, Drop } from "./components";
import { Settings, Admin } from "./Settings";
import s from "./App.module.css";
function Login({ user, onSuccess }: { user?: User; onSuccess: () => void }) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <main className={s.login}>
      <div className={s.loginCard}>
        <span className={s.brandIcon}>
          <PanelsTopLeft size={24} />
        </span>
        <h1>KKIndex</h1>
        <p>{user ? "首次使用，请更换临时密码" : "把每天安排得轻松一点"}</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            setBusy(true);
            setError("");
            const body = user
              ? { current: f.get("current"), password: f.get("password") }
              : {
                  username: f.get("username"),
                  password: f.get("password"),
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                };
            void api(user ? "/password" : "/login", "POST", body)
              .then(onSuccess)
              .catch((e) => setError(e.message))
              .finally(() => setBusy(false));
          }}
        >
          {!user && (
            <label>
              用户名
              <input
                name="username"
                autoComplete="username"
                required
                autoFocus
              />
            </label>
          )}
          {user && (
            <label>
              临时密码
              <input
                name="current"
                type="password"
                autoComplete="current-password"
                required
              />
            </label>
          )}
          <label>
            {user ? "新密码" : "密码"}
            <input
              name="password"
              type="password"
              autoComplete={user ? "new-password" : "current-password"}
              minLength={user ? 12 : undefined}
              required
            />
          </label>
          {error && (
            <p role="alert" className={s.error}>
              {error}
            </p>
          )}
          <button className={s.primary} disabled={busy}>
            {busy ? "请稍候…" : user ? "修改密码" : "登录"}
          </button>
        </form>
      </div>
    </main>
  );
}
export function App() {
  const cache = useQueryClient(),
    me = useQuery({
      queryKey: ["me"],
      queryFn: () => api<{ user: User }>("/me"),
    }),
    user = me.data?.user;
  const snapshot = useQuery({
    queryKey: ["snapshot"],
    queryFn: () => api<Snapshot>("/snapshot"),
    enabled: !!user && !user.mustChangePassword,
  });
  const items = snapshot.data?.items || [];
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "system"),
    [mobile, setMobile] = useState(matchMedia("(max-width: 800px)").matches),
    [page, setPage] = useState("calendar");
  const [sidebar, setSidebar] = useState(
      Math.max(
        35,
        Math.min(
          65,
          Number(localStorage.getItem("taskPanelWidth")) ||
            Math.max(50, Number(localStorage.getItem("sidebar")) || 0),
        ),
      ),
    ),
    [group, setGroup] = useState(localStorage.getItem("navGroup") || ""),
    [editor, setEditor] = useState<{
      item?: Entity;
      kind: Kind;
      parentId?: string;
    } | null>(null);
  const [settings, setSettings] = useState(false),
    [admin, setAdmin] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(0),
    [online, setOnline] = useState(navigator.onLine),
    [connected, setConnected] = useState(true),
    [retry, setRetry] = useState<(() => Promise<unknown>) | null>(null),
    [dragLabel, setDragLabel] = useState(""),
    [dragKind, setDragKind] = useState<Kind | null>(null),
    [siteDrop, setSiteDrop] = useState<{
      itemId: string;
      after: boolean;
    } | null>(null),
    [groupDrop, setGroupDrop] = useState<{
      itemId: string;
      after: boolean;
    } | null>(null);
  const history = useRef<string[]>([]),
    main = useRef<HTMLDivElement>(null),
    groupsElement = useRef<HTMLElement>(null),
    sitesElement = useRef<HTMLDivElement>(null),
    itemsRef = useRef(items),
    siteOpenTimer = useRef<number | null>(null),
    blockSiteOpenUntil = useRef(0),
    blockGroupClickUntil = useRef(0),
    siteDropRef = useRef<{ itemId: string; after: boolean } | null>(null),
    groupDropRef = useRef<{ itemId: string; after: boolean } | null>(null);
  itemsRef.current = items;
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);
  useEffect(() => {
    const media = matchMedia("(max-width: 800px)"),
      change = () => setMobile(media.matches),
      on = () => setOnline(true),
      off = () => setOnline(false);
    media.addEventListener("change", change);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      media.removeEventListener("change", change);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  useEffect(
    () => () => {
      if (siteOpenTimer.current !== null)
        window.clearTimeout(siteOpenTimer.current);
    },
    [],
  );
  useEffect(() => {
    if (!user || user.mustChangePassword) return;
    const es = new EventSource("/api/stream");
    const update = () => {
      void cache.invalidateQueries({ queryKey: ["snapshot"] });
      void cache.invalidateQueries({ queryKey: ["calendar"] });
    };
    es.addEventListener("change", update);
    es.addEventListener("ready", () => {
      setConnected(true);
      update();
    });
    es.addEventListener("revoked", () => {
      cache.clear();
      void me.refetch();
    });
    es.onerror = () => setConnected(false);
    return () => es.close();
  }, [user?.id, user?.mustChangePassword]);
  const execute = async (
    path: string,
    method: string,
    body?: unknown,
    mutationId = crypto.randomUUID(),
  ) => {
    if (!navigator.onLine) {
      const e = new Error("网络已断开，连接恢复后再修改");
      setError(e.message);
      throw e;
    }
    setSaving((n) => n + 1);
    setError("");
    try {
      const r = await api<{ actionId?: string; conflict?: boolean }>(
        path,
        method,
        body,
        mutationId,
      );
      if (r.actionId) {
        history.current.push(r.actionId);
        if (history.current.length > 100) history.current.shift();
      }
      if (r.conflict) setMessage("此内容已在其他设备更新，本次修改已保存");
      setRetry(null);
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["snapshot"] }),
        cache.invalidateQueries({ queryKey: ["calendar"] }),
        cache.invalidateQueries({ queryKey: ["me"] }),
      ]);
      return r;
    } catch (e) {
      setError((e as Error).message);
      if (!(e instanceof ApiError) || e.status >= 500)
        setRetry(() => () => execute(path, method, body, mutationId));
      throw e;
    } finally {
      setSaving((n) => n - 1);
    }
  };
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const [, setClock] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setClock((v) => v + 1), 60000);
    return () => clearInterval(timer);
  }, []);
  async function undoLast() {
    const actionId = history.current.at(-1);
    if (!actionId) return;
    try {
      await executeRef.current("/undo", "POST", { actionId });
      history.current.pop();
      setMessage("已撤销上一步操作");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) history.current.pop();
    }
  }
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key.toLowerCase() === "z" &&
        !(e.target as HTMLElement).closest(
          'input,textarea,[contenteditable="true"]',
        )
      ) {
        e.preventDefault();
        void undoLast();
      }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, []);
  const logout = () => {
    history.current = [];
    setEditor(null);
    setSettings(false);
    setAdmin(false);
    cache.clear();
    void me.refetch();
  };
  async function endDrag(e: DragEndEvent) {
    const source = e.operation.source;
    const manualTarget =
      source?.data.kind === "site"
        ? siteDropRef.current
        : source?.data.kind === "group"
          ? groupDropRef.current
          : null;
    if (source?.data.kind === "site")
      blockSiteOpenUntil.current = Date.now() + 350;
    if (source?.data.kind === "group")
      blockGroupClickUntil.current = Date.now() + 350;
    siteDropRef.current = null;
    groupDropRef.current = null;
    setSiteDrop(null);
    setGroupDrop(null);
    setDragLabel("");
    setDragKind(null);
    if (e.canceled) return;
    const target = e.operation.target;
    if (!source) return;
    const d = source.data,
      t = manualTarget
        ? { surface: `${source.data.kind}-order`, ...manualTarget }
        : target?.data,
      item = itemsRef.current.find((x) => x.id === d.itemId);
    if (!item || !t) return;
    try {
      if (item.kind === "parent" && t.surface === "card-layout") {
        const count = Number(t.columnCount),
          column = Number(t.column);
        if (![1, 2, 3].includes(count) || column < 0 || column >= count) return;
        const parents = itemsRef.current
          .filter((x) => x.kind === "parent" && !x.deletedAt && !x.archived)
          .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
        const before = taskColumns(parents, count);
        const columns = before.map((list) =>
          list.filter((x) => x.id !== item.id),
        );
        const destination = columns[column];
        if (t.itemId === item.id) return;
        const targetIndex = destination.findIndex((x) => x.id === t.itemId);
        destination.splice(
          targetIndex < 0
            ? destination.length
            : targetIndex + (t.after ? 1 : 0),
          0,
          item,
        );
        if (
          JSON.stringify(before.map((list) => list.map((x) => x.id))) ===
          JSON.stringify(columns.map((list) => list.map((x) => x.id)))
        )
          return;
        const ordered = columns.flat();
        await execute("/reorder", "POST", {
          ids: ordered.map((x) => x.id),
          revisions: Object.fromEntries(ordered.map((x) => [x.id, x.revision])),
          columns: Object.fromEntries(
            columns.flatMap((list, index) => list.map((x) => [x.id, index])),
          ),
        });
        return;
      }
      if (t.surface === "date" && item.kind === "task") {
        const date = String(t.date);
        const duration = item.start ? daysBetween(item.start, item.end!) : 0;
        await execute(`/items/${item.id}`, "PATCH", {
          start: date,
          end: addDays(date, duration),
          revision: item.revision,
        });
        return;
      }
      if (t.surface === "parent" && item.kind === "task") {
        await execute(`/items/${item.id}`, "PATCH", {
          parentId: t.itemId,
          order: Date.now(),
          revision: item.revision,
        });
        return;
      }
      const targetItem = itemsRef.current.find((x) => x.id === t.itemId);
      if (!targetItem) return;
      if (item.kind === "site" && targetItem.kind === "group") {
        await execute(`/items/${item.id}`, "PATCH", {
          parentId: targetItem.id,
          order: Date.now(),
          revision: item.revision,
        });
        return;
      }
      if (item.kind !== targetItem.kind || item.id === targetItem.id) return;
      const siblings = itemsRef.current
        .filter(
          (x) =>
            x.kind === item.kind &&
            !x.deletedAt &&
            !x.archived &&
            x.parentId === targetItem.parentId &&
            x.id !== item.id,
        )
        .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
      siblings.splice(
        Math.max(
          0,
          siblings.findIndex((x) => x.id === targetItem.id) + (t.after ? 1 : 0),
        ),
        0,
        item,
      );
      if (item.parentId === targetItem.parentId) {
        const before = itemsRef.current
          .filter(
            (x) =>
              x.kind === item.kind &&
              !x.deletedAt &&
              !x.archived &&
              x.parentId === item.parentId,
          )
          .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
        if (before.every((x, index) => x.id === siblings[index]?.id)) return;
      }
      await execute("/reorder", "POST", {
        ids: siblings.map((x) => x.id),
        revisions: Object.fromEntries(siblings.map((x) => [x.id, x.revision])),
        ...(item.kind === "task" || item.kind === "site"
          ? { parentId: targetItem.parentId }
          : {}),
      });
    } catch {
      /* The shared mutation handler displays errors. */
    }
  }
  function split(event: React.PointerEvent) {
    event.preventDefault();
    const move = (e: PointerEvent) => {
      const r = main.current!.getBoundingClientRect();
      const value = Math.max(
        35,
        Math.min(65, ((r.right - e.clientX) / r.width) * 100),
      );
      setSidebar(value);
      localStorage.setItem("taskPanelWidth", String(value));
    };
    const done = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", done);
      window.removeEventListener("pointercancel", done);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", done, { once: true });
    window.addEventListener("pointercancel", done, { once: true });
  }
  function moveSite(event: DragMoveEvent) {
    if (event.operation.source?.data.kind !== "site") return;
    const row = sitesElement.current;
    if (!row) return;
    const pointer = event.nativeEvent as PointerEvent | undefined;
    const x = pointer?.clientX ?? event.operation.position.current.x;
    const y = pointer?.clientY ?? event.operation.position.current.y;
    const rowRect = row.getBoundingClientRect();
    if (y < rowRect.top - 8 || y > rowRect.bottom + 8) {
      if (siteDropRef.current) {
        siteDropRef.current = null;
        setSiteDrop(null);
      }
      return;
    }
    const shells = [...row.querySelectorAll<HTMLElement>("[data-site-shell]")];
    if (!shells.length) return;
    const candidates: Array<{
      x: number;
      itemId: string;
      after: boolean;
    }> = [];
    const firstRect = shells[0].getBoundingClientRect();
    candidates.push({
      x: firstRect.left,
      itemId: shells[0].dataset.siteId!,
      after: false,
    });
    for (let index = 0; index < shells.length - 1; index++) {
      const current = shells[index],
        next = shells[index + 1],
        currentRect = current.getBoundingClientRect(),
        nextRect = next.getBoundingClientRect();
      candidates.push({
        x: (currentRect.right + nextRect.left) / 2,
        itemId: current.dataset.siteId!,
        after: true,
      });
    }
    const last = shells.at(-1)!;
    candidates.push({
      x: last.getBoundingClientRect().right,
      itemId: last.dataset.siteId!,
      after: true,
    });
    const nearest = candidates.reduce((best, candidate) =>
      Math.abs(candidate.x - x) < Math.abs(best.x - x) ? candidate : best,
    );
    const next = { itemId: nearest.itemId, after: nearest.after };
    if (
      siteDropRef.current?.itemId === next.itemId &&
      siteDropRef.current.after === next.after
    )
      return;
    siteDropRef.current = next;
    setSiteDrop(next);
  }
  function moveGroup(event: DragMoveEvent) {
    if (event.operation.source?.data.kind !== "group") return;
    const row = groupsElement.current;
    if (!row) return;
    const pointer = event.nativeEvent as PointerEvent | undefined;
    const x = pointer?.clientX ?? event.operation.position.current.x;
    const y = pointer?.clientY ?? event.operation.position.current.y;
    const rowRect = row.getBoundingClientRect();
    if (y < rowRect.top - 8 || y > rowRect.bottom + 8) {
      if (groupDropRef.current) {
        groupDropRef.current = null;
        setGroupDrop(null);
      }
      return;
    }
    const shells = [...row.querySelectorAll<HTMLElement>("[data-group-shell]")];
    if (!shells.length) return;
    const candidates: Array<{
      x: number;
      itemId: string;
      after: boolean;
    }> = [
      {
        x: shells[0].getBoundingClientRect().left,
        itemId: shells[0].dataset.groupId!,
        after: false,
      },
    ];
    for (let index = 0; index < shells.length - 1; index++) {
      const current = shells[index],
        next = shells[index + 1],
        currentRect = current.getBoundingClientRect(),
        nextRect = next.getBoundingClientRect();
      candidates.push({
        x: (currentRect.right + nextRect.left) / 2,
        itemId: current.dataset.groupId!,
        after: true,
      });
    }
    const last = shells.at(-1)!;
    candidates.push({
      x: last.getBoundingClientRect().right,
      itemId: last.dataset.groupId!,
      after: true,
    });
    const nearest = candidates.reduce((best, candidate) =>
      Math.abs(candidate.x - x) < Math.abs(best.x - x) ? candidate : best,
    );
    const next = { itemId: nearest.itemId, after: nearest.after };
    if (
      groupDropRef.current?.itemId === next.itemId &&
      groupDropRef.current.after === next.after
    )
      return;
    groupDropRef.current = next;
    setGroupDrop(next);
  }
  if (me.isPending) return <div className={s.loading}>正在打开首页…</div>;
  if (!user || user.mustChangePassword)
    return (
      <Login
        user={user}
        onSuccess={() => {
          cache.clear();
          void me.refetch();
        }}
      />
    );
  const groups = items
      .filter((e) => e.kind === "group" && !e.deletedAt)
      .sort((a, b) => a.order - b.order),
    selected = groups.find((g) => g.id === group) || groups[0];
  const open = (e: Entity) => setEditor({ item: e, kind: e.kind });
  return (
    <SelectionProvider key={user.id}>
      <DragDropProvider
        onDragStart={(e) => {
          const item = items.find(
            (x) => x.id === e.operation.source?.data.itemId,
          );
          if (item?.kind === "site") {
            if (siteOpenTimer.current !== null) {
              window.clearTimeout(siteOpenTimer.current);
              siteOpenTimer.current = null;
            }
            blockSiteOpenUntil.current = Number.POSITIVE_INFINITY;
            siteDropRef.current = null;
            setSiteDrop(null);
          }
          if (item?.kind === "group") {
            blockGroupClickUntil.current = Number.POSITIVE_INFINITY;
            groupDropRef.current = null;
            setGroupDrop(null);
          }
          setDragLabel(item?.title || "");
          setDragKind(item?.kind || null);
        }}
        onDragOver={(e) => {
          const d = e.operation.target?.data;
          if (d?.surface === "date")
            setDragLabel(`安排到 ${shortDate(String(d.date))}`);
        }}
        onDragMove={(event) => {
          moveSite(event);
          moveGroup(event);
        }}
        onDragEnd={(e) => void endDrag(e)}
      >
        <div
          className={s.app}
          data-mobile-page={page}
          style={{ "--sidebar": `${sidebar}%` } as CSSProperties}
        >
          <header className={s.navigation}>
            <div className={s.mast}>
              <div className={s.brand}>
                <span className={s.brandIcon}>
                  <PanelsTopLeft size={19} />
                </span>
                KKIndex
              </div>
              <nav
                ref={groupsElement}
                className={s.groups}
                aria-label="导航分组"
              >
                {groups.map((g) => (
                  <Drop
                    key={g.id}
                    id={`group-drop:${g.id}`}
                    data={{ surface: "group", itemId: g.id }}
                    accept={["site"]}
                  >
                    <div
                      className={s.groupShell}
                      data-group-shell
                      data-group-id={g.id}
                    >
                      <Draggable
                        item={g}
                        id={`group:${g.id}`}
                        data={{ surface: "group" }}
                        className={`${s.navGroup} ${selected?.id === g.id ? s.active : ""}`}
                        title="单击切换，双击编辑，拖动调整顺序"
                        onClick={() => {
                          if (Date.now() < blockGroupClickUntil.current) return;
                          setGroup(g.id);
                          localStorage.setItem("navGroup", g.id);
                        }}
                        onDoubleClick={(event) => {
                          event.stopPropagation();
                          if (Date.now() < blockGroupClickUntil.current) return;
                          open(g);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            setGroup(g.id);
                            localStorage.setItem("navGroup", g.id);
                          }
                          if (event.key === "F2") {
                            event.preventDefault();
                            open(g);
                          }
                        }}
                      >
                        <span>{g.title}</span>
                      </Draggable>
                      {groupDrop?.itemId === g.id && (
                        <span
                          aria-label={`${groupDrop.after ? "放在后面" : "放在前面"}：${g.title}`}
                          className={`${s.groupInsertion} ${groupDrop.after ? s.groupInsertionAfter : s.groupInsertionBefore}`}
                        />
                      )}
                    </div>
                  </Drop>
                ))}
                <button
                  aria-label="添加导航分组"
                  onClick={() => setEditor({ kind: "group" })}
                >
                  <Plus size={14} />
                </button>
              </nav>
              <div className={s.account}>
                <span className={s.saveState}>
                  {saving
                    ? "保存中…"
                    : !online
                      ? "离线"
                      : !connected
                        ? "重连中…"
                        : ""}
                </span>
                <button
                  aria-label={`主题：${theme === "system" ? "跟随系统" : theme === "light" ? "浅色" : "深色"}`}
                  onClick={() =>
                    setTheme(
                      theme === "system"
                        ? "light"
                        : theme === "light"
                          ? "dark"
                          : "system",
                    )
                  }
                >
                  {theme === "system" ? (
                    <Monitor size={17} />
                  ) : theme === "light" ? (
                    <Sun size={17} />
                  ) : (
                    <Moon size={17} />
                  )}
                </button>
                <button aria-label="个人设置" onClick={() => setSettings(true)}>
                  <SettingsIcon size={17} />
                </button>
                {user.role === "admin" && (
                  <button aria-label="系统管理" onClick={() => setAdmin(true)}>
                    <Shield size={17} />
                  </button>
                )}
                <span className={s.avatar}>{user.displayName[0]}</span>
              </div>
            </div>
            <div
              ref={sitesElement}
              className={s.sites}
              onWheel={(e) => {
                if (e.currentTarget.scrollWidth > e.currentTarget.clientWidth)
                  e.currentTarget.scrollLeft += e.deltaY;
              }}
            >
              {items
                .filter(
                  (e) =>
                    e.kind === "site" &&
                    !e.deletedAt &&
                    e.parentId === selected?.id,
                )
                .sort((a, b) => a.order - b.order)
                .map((site) => (
                  <div
                    key={site.id}
                    className={s.siteShell}
                    data-site-shell
                    data-site-id={site.id}
                  >
                    <Draggable
                      item={site}
                      id={`site:${site.id}`}
                      data={{ surface: "site" }}
                      className={s.site}
                    >
                      <a
                        href={site.url}
                        draggable={false}
                        title="单击打开，双击编辑，拖动调整顺序"
                        onClick={(event) => {
                          if (Date.now() < blockSiteOpenUntil.current) {
                            event.preventDefault();
                            return;
                          }
                          if (
                            event.ctrlKey ||
                            event.metaKey ||
                            event.shiftKey ||
                            event.altKey
                          )
                            return;
                          event.preventDefault();
                          if (siteOpenTimer.current !== null)
                            window.clearTimeout(siteOpenTimer.current);
                          siteOpenTimer.current = window.setTimeout(() => {
                            siteOpenTimer.current = null;
                            window.location.assign(site.url);
                          }, 320);
                        }}
                        onDoubleClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (Date.now() < blockSiteOpenUntil.current) return;
                          if (siteOpenTimer.current !== null) {
                            window.clearTimeout(siteOpenTimer.current);
                            siteOpenTimer.current = null;
                          }
                          open(site);
                        }}
                      >
                        {site.assetId ? (
                          <img
                            src={`/api/assets/${site.assetId}`}
                            alt=""
                            draggable={false}
                          />
                        ) : (
                          <span aria-hidden="true" className={s.siteIcon}>
                            {site.title[0]}
                          </span>
                        )}
                        <span className={s.siteTitle}>{site.title}</span>
                      </a>
                    </Draggable>
                    {siteDrop?.itemId === site.id && (
                      <span
                        aria-label={`${siteDrop.after ? "放在后面" : "放在前面"}：${site.title}`}
                        className={`${s.siteInsertion} ${siteDrop.after ? s.siteInsertionAfter : s.siteInsertionBefore}`}
                      />
                    )}
                  </div>
                ))}
              {selected ? (
                <button
                  className={s.addSite}
                  onClick={() =>
                    setEditor({ kind: "site", parentId: selected.id })
                  }
                >
                  <Plus size={16} />
                  <span>添加网站</span>
                </button>
              ) : (
                <button onClick={() => setEditor({ kind: "group" })}>
                  创建第一个导航分组
                </button>
              )}
            </div>
          </header>
          {!online && (
            <div className={s.banner}>
              <WifiOff size={15} />
              已离线，保留当前内容；恢复连接后可继续编辑。
            </div>
          )}
          {(error || message) && (
            <div
              role={error ? "alert" : "status"}
              className={`${s.banner} ${error ? s.error : ""}`}
            >
              <span>{error || message}</span>
              {retry && (
                <button onClick={() => void retry().catch(() => {})}>
                  重试
                </button>
              )}
              <button
                aria-label="关闭提示"
                onClick={() => {
                  setError("");
                  setMessage("");
                  setRetry(null);
                }}
              >
                <X size={15} />
              </button>
            </div>
          )}
          <nav className={s.mobileTabs}>
            {[
              ["calendar", "近期日程"],
              ["tasks", "任务"],
              ["navigation", "导航"],
            ].map(([id, title]) => (
              <button
                key={id}
                className={page === id ? s.active : ""}
                onClick={() => setPage(id)}
              >
                {title}
              </button>
            ))}
          </nav>
          <main ref={main} className={s.main}>
            <Calendar
              items={items}
              user={snapshot.data?.user || user}
              execute={execute}
              edit={open}
              addEvent={() => setEditor({ kind: "event" })}
              mobile={mobile}
            />
            <div
              role="separator"
              aria-label="调整日历和任务比例"
              className={s.split}
              onPointerDown={split}
            />
            <Tasks
              key={user.id}
              items={items}
              user={snapshot.data?.user || user}
              execute={execute}
              edit={open}
              addParent={() => setEditor({ kind: "parent" })}
              draggingParent={dragKind === "parent"}
              draggingTask={dragKind === "task"}
              mobile={mobile}
            />
          </main>
          {snapshot.isError && (
            <div className={s.banner}>
              任务加载失败
              <button onClick={() => void snapshot.refetch()}>重试</button>
            </div>
          )}
          {dragLabel && <div className={s.dragStatus}>{dragLabel}</div>}
          <DragOverlay>
            {dragKind !== "site" && (
              <span className={s.dragPreview}>{dragLabel}</span>
            )}
          </DragOverlay>
          {editor && (
            <Editor
              key={editor.item?.id || `${editor.kind}:${editor.parentId}`}
              {...editor}
              items={items}
              user={user}
              execute={execute}
              onClose={() => setEditor(null)}
            />
          )}
          {settings && (
            <Settings
              user={snapshot.data?.user || user}
              execute={execute}
              onClose={() => setSettings(false)}
              onLogout={logout}
            />
          )}
          {admin && <Admin onClose={() => setAdmin(false)} />}
        </div>
      </DragDropProvider>
    </SelectionProvider>
  );
}
